import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Home } from "lucide-react";
import { api } from "@/lib/api";
import { moodByKey, moodText } from "@/lib/coconMoods";
import { ProfileAvatar } from "@/components/family/ProfileEditor";

const summaryFor = (H, avg, checkins) => {
  if (!checkins) return H.none;
  if (avg >= 4) return H.bright;
  if (avg >= 3) return H.serene;
  if (avg >= 2) return H.soft;
  return H.cocoon;
};

export function HouseholdWeather() {
  useTranslation();
  const T = moodText();
  const H = T.household;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/cocon/household/week")
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setData({ days: [], explorers: [] }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const weekday = (s) => {
    try { return new Date(s + "T00:00:00").toLocaleDateString(undefined, { weekday: "narrow" }); }
    catch { return "·"; }
  };

  return (
    <div data-testid="household-weather">
      <div className="flex items-center gap-3 mb-5">
        <Home size={22} className="text-primary" />
        <div>
          <h3 className="font-display text-xl text-gradient-orange">{H.title}</h3>
          <p className="text-sm text-foreground/50">{H.desc}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-primary/60" /></div>
      ) : !data || data.explorers.length === 0 ? (
        <p className="text-foreground/50 text-sm py-4 text-center">{H.none}</p>
      ) : (
        <div className="space-y-4">
          {data.explorers.map((ex) => (
            <div key={ex.profile.id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3.5 rounded-2xl bg-white/[0.03] border border-white/10" data-testid={`household-row-${ex.profile.id}`}>
              <div className="flex items-center gap-3 sm:w-40 sm:flex-shrink-0">
                <ProfileAvatar profile={ex.profile} size={44} />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground/85 truncate">{ex.profile.name}</p>
                  <p className="text-xs text-foreground/45 truncate">{summaryFor(H, ex.avg, ex.checkins)}</p>
                </div>
              </div>
              <div className="flex justify-between sm:justify-end gap-1.5 sm:gap-2 sm:ml-auto">
                {ex.days.map((d, i) => {
                  const filled = !!d.mood;
                  const m = filled ? moodByKey(d.mood) : null;
                  return (
                    <div key={i} className="flex flex-col items-center gap-1">
                      <span
                        className={`w-7 h-7 rounded-lg flex items-center justify-center ${filled ? `bg-gradient-to-br ${m.ring}` : "border border-dashed border-white/12"}`}
                        title={filled ? T.labels[d.mood] : ""}
                      >
                        {filled && (
                          <span className="w-[calc(100%-4px)] h-[calc(100%-4px)] rounded-[0.4rem] bg-background/80 flex items-center justify-center">
                            <m.Icon size={13} className={m.text} strokeWidth={1.8} />
                          </span>
                        )}
                      </span>
                      <span className="text-[9px] text-foreground/35 uppercase">{weekday(d.date)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
